// Mini modal with picture separated by a vertical line
import React from 'react';
import { css } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import { type RestVideoRowModal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestVideoRowModal;
  close: () => void;
  detailedAction: React.ReactElement;
};

const RenderVideoV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      hero,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView, tabletView } = useMediaQueries();

  const { previewCanvas } = useCardRenderEnvironment();
  const isMobilePreview = previewCanvas?.device === 'mobile';

  const content = (
    <ModalDesktopInnerSpacer
      css={css`
        display: grid;
        ${tabletView && 'max-width: 750px;'}
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${isMobilePreview && 'height: 100%;'}
      `}
      className={cs('video', 'display-grid', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={
          <span>
            <TitleTheme data-part="title" title={title} />
          </span>
        }
        close={close}
      />
      <div
        css={css`
          position: relative;
          padding-bottom: 56.25%;
          padding-top: 25px;
          height: 0;
          box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.15);
        `}
        data-part="video"
      >
        <iframe
          title="watch-video"
          className="video-embed"
          src={hero.url}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          `}
        />
      </div>
      <div
        css={css`
          padding-top: 1rem;
        `}
        className={cs('display-grid', 'content-items', {
          desktop: !isMobilePreview && tabletView,
          mobile: mobileView || isMobilePreview,
        })}
      >
        <div
          css={css`
            padding-right: 1rem;
            overflow-y: auto;
          `}
          data-part="description"
        >
          <CustomerHtml value={details} />
        </div>
        <div>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );

  if (isMobilePreview) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {content}
      </MobileDevicePreview>
    );
  }

  return content;
};

const VideoV2 = Object.assign(RenderVideoV2, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    video: {
      path: ['requires', 'hero', 'url'],
      control: 'text',
      selector: '[data-part="video"]',
      placement: 'top-end',
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export { VideoV2 };
